import createClient from 'openapi-fetch';

import type { paths } from '@/generated/bff';

import { AppError } from './errors';

/**
 * Where the BFF lives: https://api.myciba.org in production (VITE_BFF_ORIGIN in
 * .env.production), so the SPA on developers.myciba.org calls it cross-origin.
 * Unset in development: relative, through the Vite proxy.
 */
export const BFF_ORIGIN: string = (import.meta.env.VITE_BFF_ORIGIN as string | undefined) ?? '';

const BASE = `${BFF_ORIGIN}/bff`;
const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The session's CSRF token. The BFF sends it on every response as X-CSRF-Token
 * (this origin cannot read the API host's cookies); writes echo it back.
 */
let csrfToken: string | null = null;

function remember(response: Response): Response {
    csrfToken = response.headers.get('X-CSRF-Token') ?? csrfToken;

    return response;
}

let csrfReady: Promise<void> | null = null;

/** Fetches a token once, or again after the server reported a stale one. */
function ensureCsrfToken(force = false): Promise<void> {
    if (force || csrfReady === null) {
        csrfReady = fetch(`${BASE}/csrf-cookie`, { credentials: 'include' }).then((response) => {
            remember(response);
        });
    }

    return csrfReady;
}

/**
 * The fetch the BFF client uses: the session cookie (credentials included, which
 * CORS allows for this site only), JSON, the CSRF header on writes, and one
 * automatic retry when the token went stale (419).
 */
async function bffFetch(request: Request): Promise<Response> {
    const unsafe = UNSAFE.has(request.method);

    if (unsafe && csrfToken === null) {
        await ensureCsrfToken();
    }

    const send = async (): Promise<Response> => {
        const headers = new Headers(request.headers);
        headers.set('Accept', 'application/json');
        headers.set('X-Requested-With', 'XMLHttpRequest');

        if (unsafe && csrfToken) {
            headers.set('X-CSRF-TOKEN', csrfToken);
        }

        return remember(await fetch(new Request(request.clone(), { headers, credentials: 'include' })));
    };

    let response: Response;

    try {
        response = await send();

        if (response.status === 419 && unsafe) {
            await ensureCsrfToken(true);
            response = await send();
        }
    } catch {
        throw AppError.network();
    }

    return response;
}

export const bff = createClient<paths>({ baseUrl: BASE, fetch: bffFetch });

/**
 * Multipart uploads (signed and executed MOU PDFs), with the same CSRF handling
 * and error type as every other BFF call. The browser sets the multipart boundary.
 */
export async function bffUpload<T>(path: string, form: FormData): Promise<T> {
    const response = await bffFetch(new Request(`${BASE}${path}`, { method: 'POST', body: form }));

    if (!response.ok) {
        throw await AppError.fromResponse(response);
    }

    return (await response.json()) as T;
}

/**
 * A link to a BFF URL the browser opens itself: file downloads and the staff SSO
 * redirect. Top-level navigations to the API host carry the session cookie.
 */
export function bffHref(path: string): string {
    return `${BASE}${path}`;
}

/**
 * Unwraps an openapi-fetch result: returns the data, or throws an AppError so
 * TanStack Query and forms see one error type.
 */
export async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
    const { data, error, response } = await promise;

    if (!response.ok) {
        // openapi-fetch has already read the body into `error`.
        throw await AppError.fromResponse(response, error);
    }

    return data as T;
}
